import { api, getToken, setToken, clearToken, type CharacterSummary } from "../net/api.js";
import { levelFromXp } from "@ember/shared";

const LOOKS = [
  { id: "ranger", emoji: "🏹", name: "Ranger", desc: "swift gatherer" },
  { id: "warden", emoji: "🛡️", name: "Warden", desc: "hardy fighter" },
  { id: "mystic", emoji: "🔮", name: "Mystic", desc: "keeper of fire" },
];
const LOOK_EMOJI: Record<string, string> = { ranger: "🏹", warden: "🛡️", mystic: "🔮" };
const MAX_CHARACTERS_FALLBACK = 3;

const HIGHLIGHTS = [
  { icon: "🔥", title: "Keep the fire alive", sub: "night is cold and hungry — a tended flame is life" },
  { icon: "🌑", title: "Blood Moons", sub: "every third night, the Wraith King walks" },
  { icon: "🏰", title: "Build & defend", sub: "raise walls the wraiths must claw through" },
  { icon: "🤝", title: "A shared world", sub: "one persistent realm, online with friends" },
];

/** Rising ember particles behind the title card. Returns a stop function. */
function startEmbers(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  let alive = true;

  const fit = () => {
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
  };
  fit();
  window.addEventListener("resize", fit);

  interface EmberP {
    x: number; y: number; r: number; vy: number;
    sway: number; phase: number; tint: number; tw: number;
  }
  const TINTS = ["#ff8a3d", "#ffb066", "#ffce63", "#e0556b"];
  const spawn = (anywhere = false): EmberP => ({
    x: Math.random() * canvas.width,
    y: anywhere ? Math.random() * canvas.height : canvas.height + 8,
    r: 0.8 + Math.random() * 2.4,
    vy: 14 + Math.random() * 34,
    sway: 8 + Math.random() * 22,
    phase: Math.random() * Math.PI * 2,
    tint: Math.floor(Math.random() * TINTS.length),
    tw: 1.5 + Math.random() * 3,
  });
  const parts: EmberP[] = Array.from({ length: 56 }, () => spawn(true));

  let last = performance.now();
  const frame = (now: number) => {
    if (!alive) return;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    for (let i = 0; i < parts.length; i++) {
      let p = parts[i];
      p.y -= p.vy * dt;
      if (p.y < -10) p = parts[i] = spawn();
      const x = p.x + Math.sin(now * 0.0006 * p.tw + p.phase) * p.sway;
      // brighter low (near the "fire"), fading as it climbs; flicker on top
      const climb = 1 - Math.max(0, Math.min(1, p.y / H));
      const flicker = 0.55 + 0.45 * Math.sin(now * 0.004 * p.tw + p.phase * 3);
      ctx.globalAlpha = Math.max(0, (1 - climb) * 0.75 + 0.05) * flicker;
      ctx.fillStyle = TINTS[p.tint];
      ctx.shadowColor = TINTS[p.tint];
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  return () => {
    alive = false;
    window.removeEventListener("resize", fit);
  };
}

/** Runs the title / auth / character flow. Resolves with the chosen
 *  character's id once the player is ready to enter the world. */
export function runAuthFlow(root: HTMLElement): Promise<string> {
  return new Promise((resolve) => {
    const screen = document.createElement("div");
    screen.className = "auth-screen";
    root.appendChild(screen);

    // animated ember background behind the card, torn down on resolve
    const bg = document.createElement("canvas");
    bg.className = "auth-bg";
    screen.appendChild(bg);
    const stopEmbers = startEmbers(bg);
    const content = document.createElement("div");
    content.className = "auth-content";
    screen.appendChild(content);

    let maxCharacters = MAX_CHARACTERS_FALLBACK;

    const done = (characterId: string) => {
      stopEmbers();
      screen.remove();
      resolve(characterId);
    };

    const highlightsHtml = `<div class="highlights">${HIGHLIGHTS.map(
      (h) => `<div class="hl"><span class="hl-icon">${h.icon}</span>
        <div><div class="hl-title">${h.title}</div><div class="hl-sub">${h.sub}</div></div></div>`,
    ).join("")}</div>`;

    const showLoading = (msg: string) => {
      content.innerHTML = `<div class="auth-card">
        <h1 class="title">EMBER</h1>
        <div class="loading">${msg}</div>
      </div>`;
    };

    const card = (inner: string) => {
      content.innerHTML = `<div class="auth-card">
        <h1 class="title">EMBER</h1>
        <div class="tagline">keep the fire alive</div>
        ${inner}
      </div>
      ${highlightsHtml}`;
    };

    const errEl = (msg: string) => (msg ? `<div class="error">${msg}</div>` : "");
    const infoEl = (msg: string) => (msg ? `<div class="info">${msg}</div>` : "");

    // ---------- character selection ----------
    function showSelect(characters: CharacterSummary[], error = "") {
      const slots = characters
        .map((c) => {
          const lv = Math.max(
            levelFromXp(c.gatheringXp),
            levelFromXp(c.craftingXp),
            levelFromXp(c.combatXp),
          );
          return `<div class="char-opt ${c.dead ? "fallen" : ""}" data-id="${c.id}">
            <span class="emoji">${LOOK_EMOJI[c.look] ?? "🧝"}</span>
            <div class="cname">${esc(c.name)}</div>
            <div class="cdesc">${c.dead ? "☠ fallen — rises at the fire" : `${c.look} · Lv ${lv}`}</div>
          </div>`;
        })
        .join("");
      const canCreate = characters.length < maxCharacters;
      card(`
        ${errEl(error)}
        <label style="font-size:12px;color:var(--muted);letter-spacing:1px;">CHOOSE YOUR SURVIVOR</label>
        <div class="classes chars">${slots}</div>
        ${canCreate ? `<button class="btn ghost" id="newchar">+ FORGE A NEW SURVIVOR</button>` : ""}
        <div class="switch"><a id="selectlogout">Log out</a></div>
      `);
      screen.querySelectorAll<HTMLElement>(".char-opt").forEach((el) => {
        el.onclick = () => done(el.dataset.id!);
      });
      const newBtn = screen.querySelector<HTMLButtonElement>("#newchar");
      if (newBtn) newBtn.onclick = () => showCharacter("", characters);
      screen.querySelector<HTMLElement>("#selectlogout")!.onclick = () => {
        clearToken();
        showLogin();
      };
    }

    function esc(s: string): string {
      const d = document.createElement("div");
      d.textContent = s;
      return d.innerHTML;
    }

    // ---------- character creation ----------
    let selectedLook = "ranger";
    function showCharacter(error = "", existing: CharacterSummary[] = []) {
      card(`
        ${errEl(error)}
        <div class="field">
          <label>NAME YOUR SURVIVOR</label>
          <input id="charname" maxlength="18" placeholder="Ashen" autocomplete="off" />
        </div>
        <label style="font-size:12px;color:var(--muted);letter-spacing:1px;">CHOOSE A PATH</label>
        <div class="classes">
          ${LOOKS.map(
            (l) => `<div class="class-opt ${l.id === selectedLook ? "sel" : ""}" data-look="${l.id}">
            <span class="emoji">${l.emoji}</span>
            <div class="cname">${l.name}</div>
            <div class="cdesc">${l.desc}</div>
          </div>`,
          ).join("")}
        </div>
        <button class="btn" id="enter">ENTER THE WORLD</button>
        ${existing.length ? `<div class="switch"><a id="backselect">Back to your survivors</a></div>` : ""}
      `);
      screen.querySelectorAll<HTMLElement>(".class-opt").forEach((el) => {
        el.onclick = () => {
          selectedLook = el.dataset.look!;
          screen.querySelectorAll(".class-opt").forEach((o) => o.classList.remove("sel"));
          el.classList.add("sel");
        };
      });
      const back = screen.querySelector<HTMLElement>("#backselect");
      if (back) back.onclick = () => showSelect(existing);
      const nameInput = screen.querySelector<HTMLInputElement>("#charname")!;
      nameInput.focus();
      const enter = screen.querySelector<HTMLButtonElement>("#enter")!;
      const submit = async () => {
        const name = nameInput.value.trim();
        if (name.length < 2) return showCharacter("Name must be at least 2 characters.", existing);
        enter.disabled = true;
        try {
          const created = await api.createCharacter(name, selectedLook);
          done(created.id);
        } catch (e) {
          showCharacter((e as Error).message, existing);
        }
      };
      enter.onclick = submit;
      nameInput.onkeydown = (e) => {
        if (e.key === "Enter") submit();
      };
    }

    // ---------- login ----------
    function showLogin(error = "") {
      card(`
        ${errEl(error)}
        <div class="field"><label>EMAIL</label>
          <input id="lu" type="email" autocomplete="email" placeholder="you@ember.world" /></div>
        <div class="field"><label>PASSWORD</label>
          <input id="lp" type="password" autocomplete="current-password" placeholder="••••••" /></div>
        <button class="btn" id="dologin">RETURN TO THE EMBER</button>
        <div class="switch"><a id="toforgot">Forgot password?</a></div>
        <div class="switch">New here? <a id="toreg">Forge a new soul</a></div>
      `);
      const lu = screen.querySelector<HTMLInputElement>("#lu")!;
      const lp = screen.querySelector<HTMLInputElement>("#lp")!;
      lu.focus();
      const btn = screen.querySelector<HTMLButtonElement>("#dologin")!;
      const submit = async () => {
        if (!lu.value || !lp.value) return showLogin("Enter your email and password.");
        btn.disabled = true;
        try {
          const res = await api.login(lu.value.trim(), lp.value);
          setToken(res.token);
          if (res.characters.length) showSelect(res.characters);
          else showCharacter();
        } catch (e) {
          showLogin((e as Error).message);
        }
      };
      btn.onclick = submit;
      // NB: must not `return false` here — that would preventDefault() every
      // keystroke and the field would never accept input.
      lp.onkeydown = (e) => {
        if (e.key === "Enter") submit();
      };
      screen.querySelector<HTMLElement>("#toreg")!.onclick = () => showRegister();
      screen.querySelector<HTMLElement>("#toforgot")!.onclick = () => showForgotPassword();
    }

    // ---------- password reset ----------
    function showForgotPassword(error = "", info = "") {
      card(`
        ${errEl(error)}
        ${infoEl(info)}
        <div class="field"><label>EMAIL OR USERNAME</label>
          <input id="fi" autocomplete="username" placeholder="ashen or you@ember.world" /></div>
        <button class="btn" id="doforgot">SEND RESET LINK</button>
        <div class="switch">Remembered it? <a id="backlogin">Return to the ember</a></div>
      `);
      const input = screen.querySelector<HTMLInputElement>("#fi")!;
      input.focus();
      const btn = screen.querySelector<HTMLButtonElement>("#doforgot")!;
      const submit = async () => {
        const identifier = input.value.trim();
        if (!identifier) return showForgotPassword("Enter your email or username.");
        btn.disabled = true;
        try {
          const res = await api.forgotPassword(identifier);
          const devHint = res.devToken ? ` Dev reset code: ${res.devToken}` : "";
          showForgotPassword("", `If an account exists, a reset link has been sent.${devHint}`);
        } catch (e) {
          showForgotPassword((e as Error).message);
        }
      };
      btn.onclick = submit;
      input.onkeydown = (e) => {
        if (e.key === "Enter") submit();
      };
      screen.querySelector<HTMLElement>("#backlogin")!.onclick = () => showLogin();
    }

    function showResetPassword(token: string, error = "") {
      card(`
        ${errEl(error)}
        <div class="field"><label>NEW PASSWORD</label>
          <input id="np" type="password" autocomplete="new-password" placeholder="at least 6 characters" /></div>
        <button class="btn" id="doreset">RESET PASSWORD</button>
        <div class="switch">Remembered it? <a id="resetbacklogin">Return to the ember</a></div>
      `);
      const input = screen.querySelector<HTMLInputElement>("#np")!;
      input.focus();
      const btn = screen.querySelector<HTMLButtonElement>("#doreset")!;
      const submit = async () => {
        if (input.value.length < 6) return showResetPassword(token, "Password must be at least 6 characters.");
        btn.disabled = true;
        try {
          await api.resetPassword(token, input.value);
          window.history.replaceState({}, document.title, window.location.pathname);
          showLogin("Password reset. Log in with your new password.");
        } catch (e) {
          showResetPassword(token, (e as Error).message);
        }
      };
      btn.onclick = submit;
      input.onkeydown = (e) => {
        if (e.key === "Enter") submit();
      };
      screen.querySelector<HTMLElement>("#resetbacklogin")!.onclick = () => showLogin();
    }

    // ---------- register ----------
    function showRegister(error = "") {
      card(`
        ${errEl(error)}
        <div class="field"><label>EMAIL</label>
          <input id="re" type="email" autocomplete="email" placeholder="you@ember.world" /></div>
        <div class="field"><label>USERNAME (EMAIL OK)</label>
          <input id="ru" autocomplete="username" placeholder="ashen or you@ember.world" /></div>
        <div class="field"><label>PASSWORD</label>
          <input id="rp" type="password" autocomplete="new-password" placeholder="at least 6 characters" /></div>
        <button class="btn" id="doreg">FORGE A NEW SOUL</button>
        <div class="switch">Already kindled? <a id="tolog">Return to the ember</a></div>
      `);
      const re = screen.querySelector<HTMLInputElement>("#re")!;
      const ru = screen.querySelector<HTMLInputElement>("#ru")!;
      const rp = screen.querySelector<HTMLInputElement>("#rp")!;
      re.focus();
      const btn = screen.querySelector<HTMLButtonElement>("#doreg")!;
      const submit = async () => {
        btn.disabled = true;
        try {
          const res = await api.register(re.value.trim(), ru.value.trim(), rp.value);
          setToken(res.token);
          showCharacter();
        } catch (e) {
          showRegister((e as Error).message);
        }
      };
      btn.onclick = submit;
      rp.onkeydown = (e) => {
        if (e.key === "Enter") submit();
      };
      screen.querySelector<HTMLElement>("#tolog")!.onclick = () => showLogin();
    }

    // ---------- entry: try existing session ----------
    (async () => {
      const resetToken = new URLSearchParams(window.location.search).get("reset");
      if (resetToken) return showResetPassword(resetToken);

      if (getToken()) {
        showLoading("Rekindling your session…");
        try {
          const me = await api.me();
          maxCharacters = me.maxCharacters ?? MAX_CHARACTERS_FALLBACK;
          if (me.characters.length) return showSelect(me.characters);
          return showCharacter();
        } catch {
          clearToken();
        }
      }
      showLogin();
    })();
  });
}
